import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DarkPage } from "@/components/app/PageHeader";
import { Plus, Receipt, X, ChevronDown, Trash2, CreditCard, CheckCircle2, XCircle, Link2 } from "lucide-react";

const STATUS_STYLES = {
  draft: "text-[#7d8590] bg-[#1c2128] border-[#30363d]",
  received: "text-blue-400 bg-blue-500/10 border-blue-500/20",
  partially_paid: "text-amber-400 bg-amber-500/10 border-amber-500/20",
  paid: "text-[#00F5D4] bg-[#00F5D4]/10 border-[#00F5D4]/20",
  overdue: "text-rose-400 bg-rose-500/10 border-rose-500/20",
  void: "text-[#484f58] bg-[#1c2128] border-[#30363d] line-through",
};

function fmt(n, ccy = "GBP") {
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: ccy }).format(n || 0);
  } catch {
    return `${ccy} ${(Number(n) || 0).toFixed(2)}`;
  }
}

export default function Bills() {
  const [bills, setBills] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [detail, setDetail] = useState(null);
  const [showBillModal, setShowBillModal] = useState(false);
  const [billForm, setBillForm] = useState({ supplier_id: "", project_id: "", due_date: "", supplier_reference: "", tax_rate: "20", notes: "" });
  const [saving, setSaving] = useState(false);
  const [newLine, setNewLine] = useState({ description: "", quantity: "1", unit_price: "" });
  const [payForm, setPayForm] = useState({ amount: "", payment_method: "Bank Transfer", transaction_reference: "", payment_date: new Date().toISOString().slice(0, 10) });
  const [payOpen, setPayOpen] = useState(false);
  const [payBusy, setPayBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [billRes, supRes, pRes] = await Promise.all([
        api.get("/bills"),
        api.get("/suppliers"),
        api.get("/projects").catch(() => ({ data: [] })),
      ]);
      setBills(billRes.data || []);
      setSuppliers(supRes.data || []);
      setProjects(pRes.data || []);
    } catch {
      toast.error("Failed to load bills");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadDetail = async (bill) => {
    try {
      const res = await api.get(`/bills/${bill.bill_id}`);
      setDetail(res.data);
    } catch {
      toast.error("Failed to load bill");
    }
  };

  const reloadDetail = async (bill_id) => {
    const res = await api.get(`/bills/${bill_id}`);
    setDetail(res.data);
  };

  const createBill = async () => {
    if (!billForm.supplier_id || !billForm.due_date) { toast.error("Supplier and due date required"); return; }
    setSaving(true);
    try {
      const res = await api.post("/bills", {
        supplier_id: billForm.supplier_id,
        project_id: billForm.project_id || null,
        due_date: billForm.due_date,
        supplier_reference: billForm.supplier_reference,
        tax_rate: (parseFloat(billForm.tax_rate) || 0) / 100,
        notes: billForm.notes,
      });
      toast.success("Bill created");
      setShowBillModal(false);
      setBillForm({ supplier_id: "", project_id: "", due_date: "", supplier_reference: "", tax_rate: "20", notes: "" });
      load();
      loadDetail(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create bill");
    } finally {
      setSaving(false);
    }
  };

  const addLine = async () => {
    if (!newLine.description || !newLine.unit_price) return;
    try {
      const lineItems = (detail.line_items || []).map(l => ({ description: l.description, quantity: l.quantity, unit_price: l.unit_price }));
      lineItems.push({ description: newLine.description, quantity: parseFloat(newLine.quantity) || 1, unit_price: parseFloat(newLine.unit_price) || 0 });
      await api.patch(`/bills/${detail.bill_id}`, { line_items: lineItems });
      setNewLine({ description: "", quantity: "1", unit_price: "" });
      await reloadDetail(detail.bill_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add line item");
    }
  };

  const deleteLine = async (idx) => {
    try {
      const lineItems = (detail.line_items || [])
        .filter((_, i) => i !== idx)
        .map(l => ({ description: l.description, quantity: l.quantity, unit_price: l.unit_price }));
      await api.patch(`/bills/${detail.bill_id}`, { line_items: lineItems });
      await reloadDetail(detail.bill_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove line item");
    }
  };

  const markReceived = async () => {
    try {
      await api.patch(`/bills/${detail.bill_id}`, { lifecycle_status: "received" });
      await reloadDetail(detail.bill_id);
      load();
      toast.success("Bill marked as received");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update bill");
    }
  };

  const voidBill = async () => {
    if (!window.confirm(`Void ${detail.bill_number}? This preserves the audit trail but stops it counting toward AP.`)) return;
    try {
      await api.patch(`/bills/${detail.bill_id}`, { lifecycle_status: "void" });
      await reloadDetail(detail.bill_id);
      load();
      toast.success("Bill voided");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to void bill");
    }
  };

  const deleteBill = async () => {
    if (!window.confirm(`Delete draft ${detail.bill_number}? This cannot be undone.`)) return;
    try {
      await api.delete(`/bills/${detail.bill_id}`);
      toast.success("Bill deleted");
      setDetail(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete bill");
    }
  };

  const recordPayment = async () => {
    if (!payForm.amount || Number(payForm.amount) <= 0) return toast.error("Enter a positive amount");
    setPayBusy(true);
    try {
      await api.post(`/bills/${detail.bill_id}/payments`, {
        amount: Number(payForm.amount),
        payment_method: payForm.payment_method,
        transaction_reference: payForm.transaction_reference,
        payment_date: payForm.payment_date,
      });
      toast.success("Payment recorded");
      setPayOpen(false);
      setPayForm({ ...payForm, amount: "", transaction_reference: "" });
      await reloadDetail(detail.bill_id);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to record payment");
    } finally {
      setPayBusy(false);
    }
  };

  const supName = (id) => suppliers.find(s => s.supplier_id === id)?.name || id;
  const projName = (id) => projects.find(p => p.project_id === id)?.name || id;

  if (detail) {
    const ccy = detail.currency || "GBP";
    const canRecordPayment = ["received", "partially_paid", "overdue"].includes(detail.status) && detail.balance_due > 0;
    const canDelete = detail.lifecycle_status === "draft" && (detail.payments || []).length === 0;
    const canVoid = detail.lifecycle_status !== "void" && detail.amount_paid === 0;

    return (
      <DarkPage><div className="p-6 max-w-5xl mx-auto">
        <button onClick={() => setDetail(null)} className="text-xs text-[#7d8590] hover:text-white mb-4 flex items-center gap-1">
          <ChevronDown className="w-3 h-3 rotate-90" /> Back
        </button>
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white">{detail.bill_number}</h1>
            <p className="text-sm text-[#7d8590] mt-1">
              {supName(detail.supplier_id)} · Due {detail.due_date}
              {detail.po_id && <span className="ml-2 inline-flex items-center gap-1 text-[#00F5D4]"><Link2 className="w-3 h-3" /> from PO</span>}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-xs px-3 py-1.5 rounded-full border ${STATUS_STYLES[detail.status] || STATUS_STYLES.draft}`}>{detail.status.replace("_", " ")}</span>
            {detail.lifecycle_status === "draft" && (
              <button onClick={markReceived} className="text-xs px-3 py-1.5 rounded-full border border-blue-500/20 text-blue-400 bg-blue-500/10 hover:bg-blue-500/20 transition-colors flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" /> Mark received
              </button>
            )}
            {canRecordPayment && (
              <button onClick={() => { setPayForm(f => ({ ...f, amount: String(detail.balance_due) })); setPayOpen(true); }}
                className="text-xs px-3 py-1.5 rounded-full border border-emerald-500/20 text-emerald-400 bg-emerald-500/10 hover:bg-emerald-500/20 transition-colors flex items-center gap-1">
                <CreditCard className="w-3 h-3" /> Record payment
              </button>
            )}
            {canVoid && (
              <button onClick={voidBill} className="text-xs px-3 py-1.5 rounded-full border border-[#30363d] text-[#7d8590] hover:text-white transition-colors flex items-center gap-1">
                <XCircle className="w-3 h-3" /> Void
              </button>
            )}
            {canDelete && (
              <button onClick={deleteBill} className="text-xs px-3 py-1.5 rounded-full border border-rose-500/20 text-rose-400 bg-rose-500/10 hover:bg-rose-500/20 transition-colors flex items-center gap-1">
                <Trash2 className="w-3 h-3" /> Delete
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-4 gap-4 mb-6">
          {[["Supplier", supName(detail.supplier_id)], ["Project", detail.project_id ? projName(detail.project_id) : "—"], ["Total", fmt(detail.total_amount, ccy)], ["Balance due", fmt(detail.balance_due, ccy)]].map(([k, v]) => (
            <div key={k} className="bg-[#0d1117] border border-[#21262d] rounded-lg p-4">
              <div className="text-xs text-[#7d8590] mb-1">{k}</div>
              <div className="text-white font-semibold">{v}</div>
            </div>
          ))}
        </div>

        {detail.notes && (
          <div className="bg-[#0d1117] border border-[#21262d] rounded-lg p-4 mb-4">
            <div className="text-xs text-[#7d8590] mb-1">Notes</div>
            <p className="text-sm text-white whitespace-pre-wrap">{detail.notes}</p>
          </div>
        )}

        <div className="bg-[#0d1117] border border-[#21262d] rounded-lg overflow-hidden mb-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#21262d]">
                <th className="text-left px-4 py-3 text-xs text-[#7d8590] font-medium">Description</th>
                <th className="text-right px-4 py-3 text-xs text-[#7d8590] font-medium">Qty</th>
                <th className="text-right px-4 py-3 text-xs text-[#7d8590] font-medium">Unit price</th>
                <th className="text-right px-4 py-3 text-xs text-[#7d8590] font-medium">Total</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {(detail.line_items || []).map((line, idx) => (
                <tr key={line.line_item_id || idx} className="border-b border-[#21262d] group">
                  <td className="px-4 py-3 text-white">{line.description}</td>
                  <td className="px-4 py-3 text-right text-[#7d8590]">{line.quantity}</td>
                  <td className="px-4 py-3 text-right text-[#7d8590]">{fmt(line.unit_price, ccy)}</td>
                  <td className="px-4 py-3 text-right text-white font-medium">{fmt(line.line_total, ccy)}</td>
                  <td className="px-4 py-3 text-right">
                    {detail.lifecycle_status !== "void" && (
                      <button onClick={() => deleteLine(idx)} className="opacity-0 group-hover:opacity-100 text-[#484f58] hover:text-rose-400">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {detail.lifecycle_status !== "void" && (
                <tr className="border-b border-[#21262d] bg-[#0a0d12]">
                  <td className="px-4 py-2">
                    <input value={newLine.description} onChange={e => setNewLine(v => ({ ...v, description: e.target.value }))}
                      placeholder="New line item…"
                      className="bg-transparent text-white text-sm w-full focus:outline-none placeholder:text-[#484f58]" />
                  </td>
                  <td className="px-4 py-2">
                    <input value={newLine.quantity} onChange={e => setNewLine(v => ({ ...v, quantity: e.target.value }))} type="number" min="1"
                      className="bg-transparent text-[#7d8590] text-sm text-right w-16 focus:outline-none" />
                  </td>
                  <td className="px-4 py-2">
                    <input value={newLine.unit_price} onChange={e => setNewLine(v => ({ ...v, unit_price: e.target.value }))} type="number" placeholder="0.00"
                      className="bg-transparent text-[#7d8590] text-sm text-right w-24 focus:outline-none placeholder:text-[#484f58]" />
                  </td>
                  <td className="px-4 py-2 text-right text-[#484f58] text-sm">{fmt((parseFloat(newLine.quantity) || 1) * (parseFloat(newLine.unit_price) || 0), ccy)}</td>
                  <td className="px-4 py-2 text-right">
                    <button onClick={addLine} className="text-[#00F5D4] text-xs hover:underline">Add</button>
                  </td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={3} className="px-4 py-3 text-right text-sm font-semibold text-white">Total</td>
                <td className="px-4 py-3 text-right text-sm font-bold text-white">{fmt(detail.total_amount, ccy)}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>

        <div className="bg-[#0d1117] border border-[#21262d] rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-[#21262d] text-xs text-[#7d8590] font-medium">Payments ({(detail.payments || []).length})</div>
          {(detail.payments || []).length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-[#7d8590]">No payments recorded yet.</div>
          ) : (
            <table className="w-full text-sm">
              <tbody>
                {detail.payments.map(p => (
                  <tr key={p.payment_id} className="border-b border-[#21262d] last:border-0">
                    <td className="px-4 py-2.5 text-white font-mono">{fmt(p.amount, ccy)}</td>
                    <td className="px-4 py-2.5 text-[#7d8590]">{new Date(p.payment_date).toLocaleDateString()}</td>
                    <td className="px-4 py-2.5 text-[#7d8590]">{p.payment_method}</td>
                    <td className="px-4 py-2.5 text-[#7d8590] font-mono">{p.transaction_reference || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Payment dialog */}
        {payOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
            <div className="bg-[#0d1117] border border-[#21262d] rounded-xl w-full max-w-md p-6">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-white font-semibold">Record payment</h2>
                <button onClick={() => setPayOpen(false)} className="text-[#7d8590] hover:text-white"><X className="w-5 h-5" /></button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="text-xs text-[#7d8590] mb-1 block">Amount ({ccy})</label>
                  <input type="number" step="0.01" value={payForm.amount} onChange={e => setPayForm(f => ({ ...f, amount: e.target.value }))}
                    className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm font-mono" />
                  <div className="text-[10px] text-[#7d8590] mt-1">Balance due: {fmt(detail.balance_due, ccy)}</div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-[#7d8590] mb-1 block">Date</label>
                    <input type="date" value={payForm.payment_date} onChange={e => setPayForm(f => ({ ...f, payment_date: e.target.value }))}
                      className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="text-xs text-[#7d8590] mb-1 block">Method</label>
                    <select value={payForm.payment_method} onChange={e => setPayForm(f => ({ ...f, payment_method: e.target.value }))}
                      className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm">
                      {["Bank Transfer", "Card", "Check", "Cash", "Other"].map(m => <option key={m}>{m}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-[#7d8590] mb-1 block">Transaction reference</label>
                  <input value={payForm.transaction_reference} onChange={e => setPayForm(f => ({ ...f, transaction_reference: e.target.value }))}
                    placeholder="e.g. TXN-1234"
                    className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm font-mono" />
                </div>
              </div>
              <div className="flex gap-2 mt-5">
                <Button onClick={recordPayment} disabled={payBusy} className="flex-1 bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">
                  {payBusy ? "Recording…" : "Record payment"}
                </Button>
                <Button onClick={() => setPayOpen(false)} variant="outline" className="border-[#30363d] text-[#7d8590]">Cancel</Button>
              </div>
            </div>
          </div>
        )}
      </div></DarkPage>
    );
  }

  return (
    <DarkPage><div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Bills</h1>
          <p className="text-sm text-[#7d8590] mt-1">Accounts payable — money owed to suppliers</p>
        </div>
        <Button onClick={() => { if (suppliers.length === 0) { toast.error("Add a supplier first, from the Purchase Orders page"); return; } setShowBillModal(true); }}
          className="bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">
          <Plus className="w-4 h-4 mr-1" /> New Bill
        </Button>
      </div>

      {bills.length === 0 ? (
        <div className="text-center py-20 text-[#7d8590] text-sm">
          No bills yet. Create one directly, or convert a received purchase order from the <a href="/purchase-orders" className="text-[#00F5D4] hover:underline">Purchase Orders</a> page.
        </div>
      ) : (
        <div className="bg-[#0d1117] border border-[#21262d] rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#21262d]">
                {["Bill #", "Supplier", "Project", "Status", "Due", "Total", "Balance"].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs text-[#7d8590] font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {bills.map(b => (
                <tr key={b.bill_id} onClick={() => loadDetail(b)} className="border-b border-[#21262d] hover:bg-[#161b22] cursor-pointer transition-colors">
                  <td className="px-4 py-3 text-[#00F5D4] font-mono font-medium flex items-center gap-1.5">
                    <Receipt className="w-3.5 h-3.5" /> {b.bill_number}
                  </td>
                  <td className="px-4 py-3 text-white">{supName(b.supplier_id)}</td>
                  <td className="px-4 py-3 text-[#7d8590]">{b.project_id ? projName(b.project_id) : "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_STYLES[b.status] || STATUS_STYLES.draft}`}>{b.status.replace("_", " ")}</span>
                  </td>
                  <td className="px-4 py-3 text-[#7d8590]">{b.due_date}</td>
                  <td className="px-4 py-3 text-white font-medium">{fmt(b.total_amount, b.currency)}</td>
                  <td className={`px-4 py-3 font-medium ${b.balance_due > 0 ? "text-rose-400" : "text-[#7d8590]"}`}>{fmt(b.balance_due, b.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* New bill modal */}
      {showBillModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-[#0d1117] border border-[#21262d] rounded-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-white font-semibold">New Bill</h2>
              <button onClick={() => setShowBillModal(false)} className="text-[#7d8590] hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs text-[#7d8590] mb-1 block">Supplier *</label>
                <select value={billForm.supplier_id} onChange={e => setBillForm(f => ({ ...f, supplier_id: e.target.value }))}
                  className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm">
                  <option value="">Select supplier…</option>
                  {suppliers.map(s => <option key={s.supplier_id} value={s.supplier_id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-[#7d8590] mb-1 block">Project (optional)</label>
                <select value={billForm.project_id} onChange={e => setBillForm(f => ({ ...f, project_id: e.target.value }))}
                  className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm">
                  <option value="">None</option>
                  {projects.map(p => <option key={p.project_id} value={p.project_id}>{p.name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#7d8590] mb-1 block">Due date *</label>
                  <input type="date" value={billForm.due_date} onChange={e => setBillForm(f => ({ ...f, due_date: e.target.value }))}
                    className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-xs text-[#7d8590] mb-1 block">Tax rate (%)</label>
                  <input type="number" min="0" max="100" value={billForm.tax_rate} onChange={e => setBillForm(f => ({ ...f, tax_rate: e.target.value }))}
                    className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
                </div>
              </div>
              <div>
                <label className="text-xs text-[#7d8590] mb-1 block">Supplier's invoice #</label>
                <input value={billForm.supplier_reference} onChange={e => setBillForm(f => ({ ...f, supplier_reference: e.target.value }))}
                  placeholder="Their reference for this bill"
                  className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs text-[#7d8590] mb-1 block">Notes</label>
                <textarea value={billForm.notes} onChange={e => setBillForm(f => ({ ...f, notes: e.target.value }))} rows={2}
                  className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm resize-none" />
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <Button onClick={createBill} disabled={saving} className="flex-1 bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">Create bill</Button>
              <Button onClick={() => setShowBillModal(false)} variant="outline" className="border-[#30363d] text-[#7d8590]">Cancel</Button>
            </div>
          </div>
        </div>
      )}
    </div></DarkPage>
  );
}
